import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FaBuilding, FaPlus, FaTrash, FaUsers,
  FaExclamationTriangle, FaBriefcase, FaCheckCircle, FaArrowLeft,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getApiEndpoint } from '../../../config/api';
import { getAuthHeader } from '../../../utils/auth';
import AddClientModal from './AddClientModal';
import PropertyCard from '../../property/PropertyCard/PropertyCard';
import './FmDashboardClients.css';

const FmDashboardClients = () => {
  const navigate = useNavigate();
  // The same component drives `/fm/dashboard` (clients tab) and
  // `/fm/dashboard/clients/:clientId` (detail view) — read the URL param so a
  // direct refresh on a deep link still lands on the right view.
  const { clientId } = useParams();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  // Detail-view state. Driven by the URL param so the back-button works.
  const [selectedClientId, setSelectedClientId] = useState(clientId || null);
  const [detailProperties, setDetailProperties] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => { setSelectedClientId(clientId || null); }, [clientId]);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(getApiEndpoint('/api/fm/clients'), { headers: getAuthHeader() });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setClients(Array.isArray(data?.clients) ? data.clients : []);
    } catch (_) {
      toast.error('Could not load clients');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // When a client is selected, fetch its properties.
  useEffect(() => {
    if (!selectedClientId) { setDetailProperties([]); return; }
    let cancelled = false;
    setDetailLoading(true);
    fetch(getApiEndpoint(`/api/fm/clients/${selectedClientId}/properties`), { headers: getAuthHeader() })
      .then(r => r.ok ? r.json() : { properties: [] })
      .then(d => { if (!cancelled) setDetailProperties(d.properties || []); })
      .catch(() => { if (!cancelled) setDetailProperties([]); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedClientId]);

  const handleRemove = async (cid) => {
    if (!window.confirm('Remove this client from your FM company? Their account and properties stay; only this link is removed.')) return;
    try {
      const r = await fetch(getApiEndpoint(`/api/fm/clients/${cid}`), { method: 'DELETE', headers: getAuthHeader() });
      if (!r.ok) throw new Error();
      toast.success('Client removed');
      fetchClients();
    } catch (_) { toast.error('Failed to remove client'); }
  };

  const openClient = (c) => {
    setSelectedClientId(c.id);
    navigate(`/fm/dashboard/clients/${c.id}`);
  };
  const backToList = () => {
    setSelectedClientId(null);
    navigate('/fm/dashboard');
  };

  // ── Detail view ───────────────────────────────────────────────────────────
  if (selectedClientId) {
    const client = clients.find(c => String(c.id) === String(selectedClientId));
    return (
      <div className="cs-fmcd-page">
        <button type="button" className="cs-fmcd-back-btn" onClick={backToList}>
          <FaArrowLeft /> All clients
        </button>

        {loading || detailLoading ? (
          <div className="cs-fmc-state">Loading…</div>
        ) : !client ? (
          <div className="cs-fmc-state">Client not found.</div>
        ) : (
          <>
            <div className="cs-fmcd-hero">
              <h2>{client.display_name}</h2>
              <p>
                {client.email}
                {client.company_name && client.company_name !== client.display_name && ` · ${client.company_name}`}
              </p>
              <div className="cs-fmcd-stats">
                <span className="cs-fmc-stat tone-info">
                  <FaBuilding /> {detailProperties.length} {detailProperties.length === 1 ? 'building' : 'buildings'}
                </span>
                {(client.failed_assets || 0) > 0 ? (
                  <span className="cs-fmc-stat tone-fail">
                    <FaExclamationTriangle /> {client.failed_assets} failing
                  </span>
                ) : (
                  <span className="cs-fmc-stat tone-ok">
                    <FaCheckCircle /> Compliant
                  </span>
                )}
                <span className="cs-fmc-stat tone-info">
                  <FaBriefcase /> {client.open_jobs || 0} open {(client.open_jobs || 0) === 1 ? 'job' : 'jobs'}
                </span>
              </div>
            </div>

            {detailProperties.length === 0 ? (
              <div className="cs-fmc-empty">
                <FaBuilding className="cs-fmc-empty-icon" />
                <h3>No buildings yet</h3>
                <p>This client hasn't been linked to any properties.</p>
              </div>
            ) : (
              // Use the same PropertyCard the main dashboard uses, so the client
              // detail view feels like a focused slice of /dashboard.
              <div className="properties-grid">
                {detailProperties.map(p => (
                  <PropertyCard
                    key={p.id}
                    property={{ ...p, image_url: p.image_url || null }}
                    onPropertyClick={(prop) => navigate(`/propertydashboard/${prop.id}`)}
                    viewOnly
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <div className="cs-fmc-page">
      <div className="cs-fmc-header">
        <div>
          <h2>Clients</h2>
          <p className="cs-fmc-sub">Building owners and managers your FM company looks after.</p>
        </div>
        <button type="button" className="cs-fmc-add" onClick={() => setShowAdd(true)}>
          <FaPlus /> Add client
        </button>
      </div>

      {loading ? (
        <div className="cs-fmc-state">Loading…</div>
      ) : clients.length === 0 ? (
        <div className="cs-fmc-empty">
          <FaUsers className="cs-fmc-empty-icon" />
          <h3>No clients yet</h3>
          <p>Link a building manager you look after, or invite a new client by email.</p>
          <button type="button" className="cs-fmc-add" onClick={() => setShowAdd(true)}>
            <FaPlus /> Add your first client
          </button>
        </div>
      ) : (
        <div className="cs-fmc-grid">
          {clients.map(c => (
            <button
              key={c.id}
              type="button"
              className="cs-fmc-card"
              onClick={() => openClient(c)}
            >
              <div className="cs-fmc-card-top">
                <span className="cs-fmc-card-icon"><FaBuilding /></span>
                <span className="cs-fmc-card-count">
                  {c.property_count} {c.property_count === 1 ? 'building' : 'buildings'}
                </span>
              </div>
              <h3 className="cs-fmc-card-name">{c.display_name}</h3>
              <span className="cs-fmc-card-email">{c.email}</span>
              <div className="cs-fmc-card-stats">
                {(c.failed_assets || 0) > 0 ? (
                  <span className="cs-fmc-stat tone-fail">
                    <FaExclamationTriangle /> {c.failed_assets} failing
                  </span>
                ) : (
                  <span className="cs-fmc-stat tone-ok">
                    <FaCheckCircle /> Compliant
                  </span>
                )}
                <span className="cs-fmc-stat tone-info">
                  <FaBriefcase /> {c.open_jobs || 0} open {(c.open_jobs || 0) === 1 ? 'job' : 'jobs'}
                </span>
              </div>
              <span
                className="cs-fmc-card-remove"
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); handleRemove(c.id); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleRemove(c.id); } }}
                title="Remove client"
              >
                <FaTrash />
              </span>
            </button>
          ))}
        </div>
      )}

      <AddClientModal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={() => { setShowAdd(false); fetchClients(); }}
      />
    </div>
  );
};

export default FmDashboardClients;
